/**
 * usePermission — RBAC 메뉴/액션 권한 체크 훅.
 *
 * 【역할】
 * - 로그인 사용자의 메뉴별 허용 액션 목록을 Zustand store 에 캐시.
 * - 버튼 disabled, Route 가드 등에 hasPermission(menuKey, 'C') 형태로 사용.
 *
 * 【주요 기능】
 * - loadPermissions(): /auth/permissions 조회 후 store 주입
 * - hasPermission(menuKey, action='R'): 불리언 판정
 *
 * 【RBAC 액션 코드】
 * - R=Read, C=Create, U=Update, D=Delete, E=Excel(export), P=Print
 *
 * 【내부 상태 / 캐시 구조】
 * - usePermStore: { loaded: boolean, map: Record<menuKey, Set<PermAction>> }
 * - Set 기반으로 has() 판정을 O(1) 로 수행.
 *
 * 【사용 예시】
 * ```tsx
 * const { hasPermission } = usePermission()
 * <Button disabled={!hasPermission('USER_MGMT', 'C')}>추가</Button>
 * ```
 *
 * 【왜 이 패턴?】
 * - vue-spring-fw 의 usePermission composable 포팅 — 메뉴-액션 이중 레벨 권한 모델.
 * - Zustand 로 앱 전역 1회 로드 후 모든 페이지가 구독.
 */
import { useCallback } from 'react'
import { create } from 'zustand'
import http from './api/axios'

/** 권한 액션 코드 — R/C/U/D + E(Excel)/P(Print). */
export type PermAction = 'R' | 'C' | 'U' | 'D' | 'E' | 'P'

/** 서버가 내려주는 메뉴별 허용 액션 노드. */
export interface PermissionNode {
  menuKey: string
  actions: PermAction[]
}

/** Zustand store — Set 으로 변환해 has() 판정 O(1). */
interface PermState {
  loaded: boolean
  map: Record<string, Set<PermAction>>
  setAll: (nodes: PermissionNode[]) => void
  clear: () => void
}

const usePermStore = create<PermState>((set) => ({
  loaded: false,
  map: {},
  setAll: (nodes) => {
    const map: Record<string, Set<PermAction>> = {}
    for (const n of nodes) {
      map[n.menuKey] = new Set(n.actions)
    }
    set({ map, loaded: true })
  },
  clear: () => set({ map: {}, loaded: false })
}))

/**
 * usePermission — 권한 로드/판정 API.
 * @returns { loaded, loadPermissions, hasPermission }
 */
export function usePermission() {
  const map = usePermStore((s) => s.map)
  const loaded = usePermStore((s) => s.loaded)
  const setAll = usePermStore((s) => s.setAll)

  /**
   * /auth/permissions 조회 후 store 주입 — 실패 시 빈 배열 (보수적 deny).
   */
  const loadPermissions = useCallback(async () => {
    try {
      const res = await http.get('/auth/permissions')
      const list: PermissionNode[] = res.data?.data ?? res.data ?? []
      setAll(list)
    } catch {
      setAll([])
    }
  }, [setAll])

  /**
   * 메뉴 + 액션 권한 판정 — 미로드 메뉴는 false (deny by default).
   * @param menuKey - 메뉴 식별자 (예: USER_MGMT).
   * @param action - 기본 'R' (읽기).
   */
  const hasPermission = useCallback(
    (menuKey: string, action: PermAction = 'R'): boolean => {
      const set = map[menuKey]
      if (!set) return false
      return set.has(action)
    },
    [map]
  )

  return { loaded, loadPermissions, hasPermission }
}
